/**
 * Every request that enters Verydia Control is issued a `req_...` identifier before
 * anything else happens. It is echoed in the `X-Request-ID` response header, carried
 * on every structured log line, and quoted in every Problem Details body.
 *
 * The point is operational: a customer can paste one string into a support ticket
 * and we can reconstruct exactly what happened without asking them for anything else.
 */
export const REQUEST_ID_HEADER = 'x-request-id';
export const REQUEST_ID_PREFIX = 'req';

/** Crockford base32, uppercase, excluding I/L/O/U. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const SUFFIX_LENGTH = 26;
const TIME_CHARS = 10;
const RANDOM_CHARS = SUFFIX_LENGTH - TIME_CHARS;

export const REQUEST_ID_PATTERN = /^req_[0-9A-HJKMNP-TV-Z]{26}$/;

function encodeTime(millis: number): string {
  let value = millis;
  let out = '';
  for (let i = 0; i < TIME_CHARS; i += 1) {
    out = ALPHABET[value % 32] + out;
    value = Math.floor(value / 32);
  }
  return out;
}

/**
 * ULID-shaped: a 48-bit timestamp prefix followed by 80 bits of randomness. The
 * timestamp prefix makes request identifiers sort chronologically, which is what
 * makes them useful as primary keys for a request-log table later.
 */
export function generateRequestId(now: number = Date.now(), random = Math.random): string {
  let suffix = encodeTime(now);
  for (let i = 0; i < RANDOM_CHARS; i += 1) {
    suffix += ALPHABET[Math.floor(random() * 32)];
  }
  return `${REQUEST_ID_PREFIX}_${suffix}`;
}

export function isRequestId(value: unknown): value is string {
  return typeof value === 'string' && REQUEST_ID_PATTERN.test(value);
}

/**
 * Accepts a caller-supplied request id only when it is well-formed. Clients are
 * allowed to propagate their own trace identifier, but an unvalidated header would
 * be a log-injection vector and a way to collide with somebody else's id.
 */
export function normaliseRequestId(candidate: unknown, now?: number): string {
  return isRequestId(candidate) ? candidate : generateRequestId(now);
}
